import React, { useState, useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from 'react-router-dom';
import Layout from './Layout';
import IndexCampaigns from './campaigns/Index';
import NewCampaign from './campaigns/New';
import DealsList from './deals/list/DealsList.view';
import DealFormPage from './deals/form/DealFormPage';
import ProfileStatsPage from './profile/ProfileStatsPage';
import ProfileAboutPage from './profile/ProfileAboutPage';
import Scorecard from './scorecard/Scorecard';
import NotFound from './NotFound';
import Dashboard from './dashboards/Dashboard.view';
import SelectChart from './charts/SelectChart';
import NewReward from './rewards/NewReward';
import UsersScreen from './users/UsersScreen';
import OrgsList from './config/OrgsList';
import OrgConfigPage from './config/OrgConfigPage';
import Games from './games';
import DevComp from './config/DevComp';
import TrainingHomePage from './training/TrainingHomePage';
import TrainingCategoryPage from './training/TrainingCategoryPage';
import TrainingVideoPage from './training/TrainingVideoPage';
import Feed from './feed';
import Comments from './feed/Comments';
import NewIncentive from './games/NewIncentive';
import RewardsList from './rewards/RewardsList';
import TeamsScreen from './teams/TeamsScreen';
import TeamPage from './teams/TeamPage';
import TeamFormPage from './teams/TeamFormPage';
import RewardDetails from './rewards/RewardDetails';
import ReportsScreen from './reports/ReportsScreen';
import DataImports from './data-imports/DataImports';
import UserFormPage from './users/UserFormPage';
import PerformanceSideBar from './performance/PerformanceSideBar.view';
import ProductsScreen from './products/ProductsScreen';
import DealsKanban from './deals/kanban/DealsKanban.view';
import PipelineScreen from './pipeline/PipelineScreen.view';
import PaymentsSideBar from './payments/PaymentsSideBar.view';
import Transactions from './payments/paymentsTabs/Transactions.view';
import BatchSettlements from './payments/paymentsTabs/BatchSettlements.view';
import Payouts from './payments/paymentsTabs/Payouts.view';
import MerchantStatements from './payments/paymentsTabs/MerchantStatements.view';
import MerchantReports from './payments/paymentsTabs/MerchantReports';
import { SettingsPage } from './settings/SettingsPage';
import { SchedulingScreen } from './scheduling/SchedulingScreen';
import Leaderboard from './leaderboard';
import { Typography, Box } from '@mui/material';
import { APP_VERSION } from '../constants/version';
import ChangelogModal from '../components/ChangelogModal';
import ActivityAnalytics from './activity';
import GameDetailsView from './games/GameDetails/GameDetails.view';
import MapPage from './map/MapPage';
import FeatureFlagRoute from '../common/FeatureFlagRoute';
import WebInterfaceRoute from '../common/WebInterfaceRoute';
import RewardsArchiveView from './performance/RewardsArchive';
import DealDetailPage from './deal/DealDetailPage';
import { isStarterAccount } from '../common/utils/isStarterAccount';
import useOrgStore from '../store/zustand/useOrgStore';

function InitialRedirect() {
  const navigate = useNavigate();
  const [hasRedirected, setHasRedirected] = useState(false);
  const orgData = useOrgStore((state) => state.orgData);

  useEffect(() => {
    // @ts-ignore
    if ((global as any)?.me?.id && !hasRedirected) {
      const webInterfaceConfig =
        orgData?.configParms?.features?.webInterface ||
        // @ts-ignore
        (global as any)?.appSettings?.features?.webInterface ||
        {};
      const hideFeed = webInterfaceConfig.hideFeed === true;
      const destination =
        isStarterAccount() || hideFeed ? '/pipeline' : '/feed';
      navigate(destination, { replace: true });
      setHasRedirected(true);
    }
    // @ts-ignore
  }, [(global as any)?.me?.id, (global as any)?.me?.clonedFrom, orgData]);

  return null;
}

export default function RouteConfiguration() {
  return (
    <BrowserRouter>
      <RouteWithBG />
    </BrowserRouter>
  );
}

function RouteWithBG() {
  const [openChangelog, setOpenChangelog] = useState(false);

  return (
    <Box sx={(theme) => ({ backgroundColor: theme.custom?.background1 })}>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<InitialRedirect />} />
          <Route path="performance" element={<PerformanceSideBar />}>
            <Route path="leaderboard" element={<Leaderboard />} />
            <Route path="games" element={<Games />} />
            <Route path="games/:gameId" element={<GameDetailsView />} />
            <Route path="games/new-incentive" element={<NewIncentive />} />
            <Route path="rewards" element={<RewardsList />} />
            <Route path="rewards/new" element={<NewReward />} />
            <Route path="rewards/edit/:rewardId" element={<NewReward />} />
            <Route path="rewards/details" element={<RewardDetails />} />
            <Route path="rewards/archive" element={<RewardsArchiveView />} />
            <Route path="activity" element={<ActivityAnalytics />} />
            <Route path="reports" element={<ReportsScreen />} />
          </Route>
          <Route path="scorecard/:userId" element={<Scorecard />} />
          <Route path="campaigns" element={<IndexCampaigns />} />
          <Route
            path="charts"
            // @ts-ignore
            element={<SelectChart userID={global?.me?.id} />}
          />
          <Route path="training" element={<TrainingHomePage />} />
          <Route path="training/:category" element={<TrainingCategoryPage />} />
          <Route
            path="training/:category/:lessonId"
            element={<TrainingVideoPage />}
          />
          <Route path="devtool" element={<DevComp />} />
          <Route path="pipeline" element={<PipelineScreen />}>
            <Route path="board" element={<DealsKanban />} />
            <Route path="board/:dealId" element={<DealsKanban />} />
            <Route path="list" element={<DealsList />} />
            <Route path="list/:dealId" element={<DealsList />} />
            <Route path="new" element={<DealFormPage />} />
            <Route path="edit/:dealId" element={<DealFormPage />} />
          </Route>
          <Route element={<WebInterfaceRoute flag="hideFeed" />}>
            <Route path="feed" element={<Feed />} />
            <Route path="feed/comments" element={<Comments />} />
          </Route>
          <Route path="campaigns/new" element={<NewCampaign />} />
          <Route path="people" element={<UsersScreen />} />
          <Route path="people/new" element={<UserFormPage />} />
          <Route path="people/edit/:userId" element={<UserFormPage />} />
          <Route path="people/stats/:userId" element={<ProfileStatsPage />} />
          <Route path="people/about/:userId" element={<ProfileAboutPage />} />
          <Route path="teams" element={<TeamsScreen />} />
          <Route path="teams/:teamId" element={<TeamPage />} />
          <Route path="teams/new" element={<TeamFormPage />} />
          <Route path="teams/edit/:teamId" element={<TeamFormPage />} />
          <Route element={<WebInterfaceRoute flag="hideProducts" />}>
            <Route path="products" element={<ProductsScreen />} />
          </Route>
          <Route path="dashboard" element={<Dashboard />} />
          <Route element={<FeatureFlagRoute featureName="webMap" />}>
            <Route path="map" element={<MapPage />} />
          </Route>
          <Route path="payments" element={<PaymentsSideBar />}>
            <Route path="transactions" element={<Transactions />} />
            <Route path="batch-settlements" element={<BatchSettlements />} />
            <Route path="payouts" element={<Payouts />} />
            <Route
              path="merchant-statements"
              element={<MerchantStatements />}
            />
            <Route path="merchant-reports" element={<MerchantReports />} />
          </Route>
          <Route path="admin-config" element={<OrgsList />} />
          <Route path="admin-config/:orgID" element={<OrgConfigPage />} />
          <Route path="imports" element={<DataImports />} />
          <Route
            path="/settings"
            element={<Navigate to="/settings/general" replace />}
          />
          <Route path="settings/*" element={<SettingsPage />} />
          <Route element={<WebInterfaceRoute flag="disableScheduling" />}>
            <Route path="scheduling" element={<SchedulingScreen />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route path="deal" element={<DealDetailPage />} />
        <Route path="deal/:dealId" element={<DealDetailPage />} />
      </Routes>
      <Typography
        sx={(theme) => ({
          fontSize: 12,
          textAlign: 'center',
          margin: 1,
          cursor: 'pointer',
          color: theme.custom?.textLight,
          '&:hover': {
            textDecoration: 'underline',
          },
        })}
        onClick={() => setOpenChangelog(true)}
      >
        {APP_VERSION}
      </Typography>
      <ChangelogModal
        open={openChangelog}
        onClose={() => setOpenChangelog(false)}
      />
    </Box>
  );
}
